import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  Building, Trash2, CheckCircle, Search, Filter, 
  ChevronUp, ChevronDown, User, ExternalLink, Eye, X, 
  Briefcase, Award, Phone, Mail, MapPin, DollarSign, 
  FileText, Check, Clock, Server, Layers, Cpu, MessageSquare,
  Plus, Pin, ShieldCheck, Radio, Wifi, Database, Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TcmDetailDrawer = ({ tenant, onClose, onUpdateStatus }) => {
  if (!tenant) return null;

  const workspaceName = tenant.workspaceName || tenant.orgName || 'TCM Enterprise Workspace';
  const subdomain = tenant.subdomain || tenant.subdomainSlug || 'ops';
  const tier = tenant.tier || (tenant.seatsCount > 50 ? 'orbit' : tenant.seatsCount > 10 ? 'sphere' : 'circle');

  // Atomic Math Calculation (Strict KES 1,500 / seat minimum)
  const seatCount = parseInt(tenant.seatsCount || tenant.seats) || 1;
  const expectedMonthlyFee = seatCount * 1500;
  const expectedSetupFee = seatCount <= 5 ? 5000 : seatCount <= 50 ? 10000 : seatCount <= 500 ? 25000 : 50000;
  const totalExpectedFirstBilling = expectedMonthlyFee + expectedSetupFee;
  const actualMonthlyFee = parseInt(tenant.monthlyUsageFee) || expectedMonthlyFee;
  const actualSetupFee = parseInt(tenant.setupFee) || expectedSetupFee;
  const actualTotal = actualMonthlyFee + actualSetupFee;
  const passesAtomicAudit = actualMonthlyFee >= expectedMonthlyFee;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[680px] lg:w-[780px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300 text-white">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/60 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-pink-500/10 text-pink-400 border border-pink-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 05 &bull; Tasks and Customer Management (TCM) Workspace
              </span>
              <span className="text-xs font-mono font-bold text-amber-400 uppercase">
                {tier} Tier
              </span>
            </div>
            <h2 className="text-2xl font-black text-white">{workspaceName}</h2>
            <p className="text-xs text-zinc-400 font-mono mt-1 flex items-center gap-2">
              <Server size={14} className="text-pink-400" /> {subdomain}.dynamicindutechno.solutions
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Status</span>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                {tenant.status || 'Active'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Operational Seats</span>
              <span className="text-xs font-bold text-white font-mono">
                {seatCount} Allocated Seats
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Monthly Billing</span>
              <span className="text-xs font-bold text-pink-400 font-mono">
                KES {actualMonthlyFee.toLocaleString()} / mo
              </span>
            </div>
          </div>

          {/* Atomic Pricing Corroboration Engine */}
          <div className={`border rounded-2xl p-5 space-y-4 ${passesAtomicAudit ? 'bg-emerald-950/20 border-emerald-800/60' : 'bg-red-950/20 border-red-800/60'}`}>
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <h3 className="text-xs font-black uppercase tracking-widest text-white flex items-center gap-2">
                <ShieldCheck size={16} className={passesAtomicAudit ? 'text-emerald-400' : 'text-red-400'} />
                Atomic Billing Corroboration Engine
              </h3>
              <span className={`text-[10px] font-mono font-black uppercase px-2.5 py-0.5 rounded-full border ${passesAtomicAudit ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}`}>
                {passesAtomicAudit ? 'AUDIT PASSED: KES 1,500/SEAT COMPLIANT' : 'AUDIT WARNING: UNDERBILLED'}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="p-3 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-500 font-bold block text-[10px] uppercase">Allocated Seats</span>
                <span className="text-white font-mono font-bold text-sm">{seatCount} Seats</span>
              </div>
              <div className="p-3 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-500 font-bold block text-[10px] uppercase">Expected Monthly</span>
                <span className="text-pink-400 font-mono font-bold text-sm">KES {expectedMonthlyFee.toLocaleString()}</span>
                <span className="text-[10px] text-zinc-500 block">({seatCount} &times; KES 1,500)</span>
              </div>
              <div className="p-3 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-500 font-bold block text-[10px] uppercase">Expected Setup</span>
                <span className="text-amber-400 font-mono font-bold text-sm">KES {expectedSetupFee.toLocaleString()}</span>
                <span className="text-[10px] text-zinc-500 block">Tier: {tier.toUpperCase()}</span>
              </div>
              <div className="p-3 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-500 font-bold block text-[10px] uppercase">Expected First Bill</span>
                <span className="text-emerald-400 font-mono font-bold text-sm">KES {totalExpectedFirstBilling.toLocaleString()}</span>
                <span className="text-[10px] text-zinc-500 block">Setup + 1st Month</span>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs pt-1 border-t border-zinc-800/60">
              <div className="text-zinc-400">
                Recorded In Application: <span className="text-white font-mono font-bold">KES {actualMonthlyFee.toLocaleString()} / mo</span> + Setup <span className="text-white font-mono font-bold">KES {actualSetupFee.toLocaleString()}</span>
              </div>
              <div className="text-[11px] font-mono text-zinc-500">
                Strict KES 1,500 / seat minimum policy
              </div>
            </div>
          </div>

          {/* Section 1: Tenant Identity & Subdomain */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Building size={14} /> Organization &amp; Subdomain Routing
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Organization Name:</span>
                <span className="text-zinc-200 font-bold">{tenant.orgName || workspaceName}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Organization Type:</span>
                <span className="text-zinc-200">{tenant.orgType || 'Municipal Utility / Public Service'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Registration / Charter No:</span>
                <span className="text-zinc-200 font-mono">{tenant.orgRegNo || tenant.regNo || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Dedicated Subdomain:</span>
                <span className="text-pink-300 font-mono font-bold">{subdomain}.dynamicindutechno.solutions</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Official Website:</span>
                <span className="text-zinc-200 font-mono">{tenant.orgWebsite || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Setup Fee:</span>
                <span className="text-zinc-200 font-mono font-bold">KES {actualSetupFee.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Physical Deployment & Field Scope */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <MapPin size={14} /> Physical Deployment &amp; Jurisdictional Scope
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Operating Country:</span>
                <span className="text-zinc-200">{tenant.depCountry || 'Kenya'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Primary County / Jurisdiction:</span>
                <span className="text-zinc-200 font-bold">{tenant.depCounty || tenant.county || '-'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-zinc-500 font-bold block">Headquarters Physical Address:</span>
                <span className="text-zinc-200">{tenant.depAddress || tenant.address || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Stations / Field Depots:</span>
                <span className="text-zinc-200">{tenant.depBranches || 'Single Central Station'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Field Staff Cadre:</span>
                <span className="text-zinc-200">{tenant.depCadre || 'Technicians & Field Officers'}</span>
              </div>
            </div>
          </div>

          {/* Section 3: Operations Modules */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Cpu size={14} /> Operations Engine Modules
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center justify-between p-2.5 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-400">AI Incident Triage</span>
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${tenant.aiTriageModule !== false && tenant.aiTriageModule !== 'Disabled' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-zinc-800 text-zinc-500'}`}>
                  {tenant.aiTriageModule !== false && tenant.aiTriageModule !== 'Disabled' ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between p-2.5 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-400">SMS Dispatch Gateway</span>
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${tenant.smsBroadcastModule !== false && tenant.smsBroadcastModule !== 'Disabled' && tenant.smsEscalationModule !== false ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-zinc-800 text-zinc-500'}`}>
                  {tenant.smsBroadcastModule !== false && tenant.smsBroadcastModule !== 'Disabled' && tenant.smsEscalationModule !== false ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between p-2.5 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-400">Offline PWA Sync</span>
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${tenant.offlineSyncModule !== false && tenant.offlineSyncModule !== 'Disabled' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-zinc-800 text-zinc-500'}`}>
                  {tenant.offlineSyncModule !== false && tenant.offlineSyncModule !== 'Disabled' ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between p-2.5 bg-zinc-900 rounded-xl border border-zinc-800">
                <span className="text-zinc-400">Sensor &amp; Telemetry Ingest</span>
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${tenant.telemetryModule !== false && tenant.telemetryModule !== 'Disabled' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-zinc-800 text-zinc-500'}`}>
                  {tenant.telemetryModule !== false && tenant.telemetryModule !== 'Disabled' ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {/* Section 4: Primary Administrator Credentials */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <User size={14} /> SuperAdmin Credentials
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">SuperAdmin Name:</span>
                <span className="text-zinc-200 font-bold">{tenant.primaryAdminName || tenant.adminFullName || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Job Title:</span>
                <span className="text-zinc-200">{tenant.primaryAdminJobTitle || tenant.adminJobTitle || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Official Email:</span>
                <span className="text-zinc-200 font-mono">{tenant.primaryAdminEmail || tenant.email || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Direct Phone:</span>
                <span className="text-zinc-200 font-mono">{tenant.primaryAdminPhone || tenant.adminPhone || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">National ID / Passport:</span>
                <span className="text-zinc-200 font-mono">{tenant.primaryAdminNationalId || tenant.adminNationalId || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Payment Ref / Transaction:</span>
                <span className="text-amber-400 font-mono font-bold">{tenant.paymentReference || tenant.transactionRef || '-'}</span>
              </div>
            </div>
          </div>

          {/* Compliance Documents */}
          {(tenant.certIncorporationUrl || tenant.kraPinUrl || tenant.complianceCertUrl) && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
                <FileText size={14} /> Verification &amp; Compliance Documents
              </h3>
              <div className="flex flex-wrap gap-2 text-xs">
                {tenant.certIncorporationUrl && (
                  <a href={tenant.certIncorporationUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-1 font-bold">
                    <ExternalLink size={12} /> Incorporation Charter
                  </a>
                )}
                {tenant.kraPinUrl && (
                  <a href={tenant.kraPinUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-1 font-bold">
                    <ExternalLink size={12} /> KRA PIN Certificate
                  </a>
                )}
                {tenant.complianceCertUrl && (
                  <a href={tenant.complianceCertUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-1 font-bold">
                    <ExternalLink size={12} /> Regulatory License
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Admin Status & Approval Actions */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Workspace Approval &amp; Lifecycle</span>
            <div className="flex gap-2">
              <button 
                onClick={() => {
                  if (!passesAtomicAudit) {
                    if (!window.confirm(`Warning: This tenant is billed KES ${actualMonthlyFee.toLocaleString()} which is below the atomic minimum of KES ${expectedMonthlyFee.toLocaleString()} (${seatCount} seats @ KES 1,500). Proceed with approval anyway?`)) return;
                  }
                  onUpdateStatus(tenant.id, 'active');
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1.5 ${tenant.status === 'active' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                <CheckCircle size={13} />
                <span>Approve &amp; Activate</span>
              </button>
              <button 
                onClick={() => onUpdateStatus(tenant.id, 'pending_review')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${tenant.status === 'pending_review' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Pending Review
              </button>
              <button 
                onClick={() => onUpdateStatus(tenant.id, 'suspended')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${tenant.status === 'suspended' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Suspended
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const TcmManager = ({ showDemo, setShowDemo }) => {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTier, setFilterTier] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [pinnedColumn, setPinnedColumn] = useState('workspaceName');
    const [statusMessage, setStatusMessage] = useState('');

    // Comprehensive Creation Form State (Matching 100% of onboarding-tcm.html)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [orgName, setOrgName] = useState('');
    const [orgType, setOrgType] = useState('Municipal Water & Sanitation Utility');
    const [subdomainSlug, setSubdomainSlug] = useState('');
    const [orgRegNo, setOrgRegNo] = useState('');
    const [orgWebsite, setOrgWebsite] = useState('');

    const [depCountry, setDepCountry] = useState('Kenya');
    const [depCounty, setDepCounty] = useState('Kericho');
    const [depAddress, setDepAddress] = useState('');
    const [depBranches, setDepBranches] = useState('Single Central Station (1)');
    const [depCadre, setDepCadre] = useState('Technicians & Field Officers');

    const [seatsCount, setSeatsCount] = useState(30);
    const [tier, setTier] = useState('sphere');
    const [setupFee, setSetupFee] = useState(2000);
    const [monthlyUsageFee, setMonthlyUsageFee] = useState(2000);
    const [aiTriageModule, setAiTriageModule] = useState('Enabled');
    const [smsBroadcastModule, setSmsBroadcastModule] = useState('Enabled');
    const [offlineSyncModule, setOfflineSyncModule] = useState('Enabled');
    const [telemetryModule, setTelemetryModule] = useState('Enabled');

    const [adminFullName, setAdminFullName] = useState('');
    const [adminOfficialEmail, setAdminOfficialEmail] = useState('');
    const [adminPhone, setAdminPhone] = useState('');
    const [adminJobTitle, setAdminJobTitle] = useState('Operations Director');
    const [adminNationalId, setAdminNationalId] = useState('');

    const [certIncorporationUrl, setCertIncorporationUrl] = useState('');
    const [kraPinUrl, setKraPinUrl] = useState('');
    const [complianceCertUrl, setComplianceCertUrl] = useState('');
    const [paymentReference, setPaymentReference] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('Paid');

    // Dynamic fee calculator based on seats slider (Strict KES 1,500/seat minimum)
    const handleSeatChange = (seats) => {
      const num = parseInt(seats) || 1;
      setSeatsCount(num);
      if (num <= 5) {
        setTier('circle'); // Starter Syndicate (1 to 5 seats)
        setSetupFee(5000);
        setMonthlyUsageFee(num * 1500); // KES 1,500/seat (5 seats = KES 7,500)
      } else if (num <= 50) {
        setTier('sphere'); // Growth Operations (30 to 50 seats)
        setSetupFee(10000);
        setMonthlyUsageFee(num * 1500); // KES 1,500/seat (30 seats = KES 45,000, 50 seats = KES 75,000)
      } else if (num <= 500) {
        setTier('orbit'); // Institutional Core (100 to 500 seats)
        setSetupFee(25000);
        setMonthlyUsageFee(num * 1500); // KES 1,500/seat (100 seats = KES 150,000, 500 seats = KES 750,000)
      } else {
        setTier('sovereign_custom'); // Sovereign Megascale (500+ seats: Logistics Desk)
        setSetupFee(50000);
        setMonthlyUsageFee(num * 1500);
      }
    };

    const fetchTenants = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'enterprise_tenants'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));

            if (showDemo) {
              data.unshift(
                {
                  id: 'chewsco-water',
                  workspaceName: 'Chemosit Water & Sanitation Co.',
                  orgName: 'Chemosit Water & Sanitation Co. (CHEWSCO)',
                  orgType: 'Municipal Water & Sanitation Utility',
                  orgRegNo: 'WASREB/2019/042',
                  orgWebsite: 'https://chewsco.co.ke',
                  subdomain: 'chewsco',
                  subdomainSlug: 'chewsco',
                  depCountry: 'Kenya',
                  depCounty: 'Kericho',
                  depAddress: 'Chemosit Water Works Road, Block B',
                  depBranches: '2-5 Regional Stations',
                  depCadre: 'Technicians & Field Officers',
                  tier: 'orbit',
                  seatsCount: 85,
                  setupFee: 25000,
                  monthlyUsageFee: 425000,
                  primaryAdminName: 'Eng. Kipkemoi Ruto',
                  adminFullName: 'Eng. Kipkemoi Ruto',
                  primaryAdminEmail: 'operations@chewsco.co.ke',
                  primaryAdminPhone: '+254 722 000 111',
                  adminJobTitle: 'Chief Technical Director',
                  adminNationalId: '22883311',
                  status: 'active',
                  aiTriageModule: true,
                  smsBroadcastModule: true,
                  offlineSyncModule: true,
                  telemetryModule: true,
                  paymentReference: 'MPESA-QWR88119X',
                  addedAt: '2026-08-15T09:30:00Z'
                },
                {
                  id: 'baringo-agri',
                  workspaceName: 'Baringo County Agri-Logistics',
                  orgName: 'Department of Agriculture & Livestock',
                  orgType: 'County Government Dept',
                  orgRegNo: 'CG-BAR-AGRI-003',
                  orgWebsite: 'https://baringo.go.ke',
                  subdomain: 'baringo-agri',
                  subdomainSlug: 'baringo-agri',
                  depCountry: 'Kenya',
                  depCounty: 'Baringo',
                  depAddress: 'County Executive Headquarters, Kabarnet',
                  depBranches: '6-15 Field Depots',
                  depCadre: 'Mixed Field & Office Desk Staff',
                  tier: 'sphere',
                  seatsCount: 25,
                  setupFee: 10000,
                  monthlyUsageFee: 125000,
                  primaryAdminName: 'Dr. Stella Chebet',
                  adminFullName: 'Dr. Stella Chebet',
                  primaryAdminEmail: 'agriculture@baringo.go.ke',
                  primaryAdminPhone: '+254 711 222 333',
                  adminJobTitle: 'Director of Field Logistics',
                  adminNationalId: '19445588',
                  status: 'active',
                  aiTriageModule: true,
                  smsBroadcastModule: true,
                  offlineSyncModule: false,
                  telemetryModule: true,
                  paymentReference: 'EXEMPT-DEVOLUTION',
                  addedAt: '2026-09-01T11:00:00Z'
                }
              );
            }
            setTenants(data);
        } catch (e) {
            console.error("Error fetching enterprise tenants:", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchTenants();
    }, [showDemo]);

    const handleCreateTenant = async (e) => {
      e.preventDefault();
      if (!orgName.trim() || !subdomainSlug.trim()) return;
      setStatusMessage('saving');

      const tenantId = subdomainSlug.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

      try {
        await setDoc(doc(db, 'enterprise_tenants', tenantId), {
          workspaceName: orgName.trim(),
          orgName: orgName.trim(),
          orgType,
          subdomain: subdomainSlug.trim().toLowerCase(),
          subdomainSlug: subdomainSlug.trim().toLowerCase(),
          orgRegNo: orgRegNo.trim(),
          orgWebsite: orgWebsite.trim(),
          depCountry: depCountry.trim(),
          depCounty: depCounty.trim(),
          depAddress: depAddress.trim(),
          depBranches,
          depCadre,
          seatsCount: parseInt(seatsCount) || 10,
          tier,
          setupFee: parseInt(setupFee) || 5000,
          monthlyUsageFee: parseInt(monthlyUsageFee) || ((parseInt(seatsCount) || 1) * 1500),
          aiTriageModule: aiTriageModule === 'Enabled',
          smsBroadcastModule: smsBroadcastModule === 'Enabled',
          smsEscalationModule: smsBroadcastModule === 'Enabled',
          offlineSyncModule: offlineSyncModule === 'Enabled',
          telemetryModule: telemetryModule === 'Enabled',
          primaryAdminName: adminFullName.trim(),
          adminFullName: adminFullName.trim(),
          primaryAdminEmail: adminOfficialEmail.trim().toLowerCase(),
          primaryAdminPhone: adminPhone.trim(),
          adminPhone: adminPhone.trim(),
          primaryAdminJobTitle: adminJobTitle.trim(),
          adminJobTitle: adminJobTitle.trim(),
          primaryAdminNationalId: adminNationalId.trim(),
          adminNationalId: adminNationalId.trim(),
          certIncorporationUrl: certIncorporationUrl.trim(),
          kraPinUrl: kraPinUrl.trim(),
          complianceCertUrl: complianceCertUrl.trim(),
          paymentReference: paymentReference.trim(),
          paymentStatus,
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }, { merge: true });

        // Reset Form
        setOrgName('');
        setSubdomainSlug('');
        setOrgRegNo('');
        setOrgWebsite('');
        setDepAddress('');
        setAdminFullName('');
        setAdminOfficialEmail('');
        setAdminPhone('');
        setAdminNationalId('');
        setPaymentReference('');
        setIsFormOpen(false);

        setStatusMessage('success');
        fetchTenants();
        setTimeout(() => setStatusMessage(''), 3000);
      } catch (err) {
        console.error("Failed to provision workspace:", err);
        setStatusMessage('error');
      }
    };

    const handleUpdateStatus = async (tenantId, newStatus) => {
      try {
        await setDoc(doc(db, 'enterprise_tenants', tenantId), {
          status: newStatus,
          statusUpdatedAt: new Date().toISOString()
        }, { merge: true });
        
        setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status: newStatus } : t));
        if (selectedTenant && selectedTenant.id === tenantId) {
          setSelectedTenant(prev => ({ ...prev, status: newStatus }));
        }
      } catch (err) {
        console.error("Failed to update status:", err);
      }
    };

    const handleDelete = async (tenantId) => {
        if (!window.confirm(`Permanently remove TCM workspace ${tenantId}?`)) return;
        try {
            await deleteDoc(doc(db, 'enterprise_tenants', tenantId));
            if (selectedTenant?.id === tenantId) setSelectedTenant(null);
            fetchTenants();
        } catch (error) {
            console.error(error);
        }
    };

    const filteredTenants = tenants.filter(t => {
      const matchesTier = filterTier === 'all' || (t.tier || '').toLowerCase() === filterTier.toLowerCase();
      const query = searchTerm.toLowerCase();
      const name = (t.workspaceName || t.orgName || '').toLowerCase();
      const sub = (t.subdomain || t.subdomainSlug || '').toLowerCase();
      const admin = (t.primaryAdminName || t.adminFullName || t.primaryAdminEmail || '').toLowerCase();
      const countyVal = (t.depCounty || t.county || '').toLowerCase();
      const matchesSearch = !searchTerm || name.includes(query) || sub.includes(query) || admin.includes(query) || countyVal.includes(query);
      return matchesTier && matchesSearch;
    });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedTenants = [...filteredTenants].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        let bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-pink-500/20 text-pink-400 border border-pink-500/30 rounded-xl">
                                <Server size={18} />
                            </span>
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Portal 05 &bull; Tasks and Customer Management (TCM)
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight">Tasks and Customer Management (TCM) Workspaces</h1>
                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2 max-w-xl">
                            Multi-tenant operations rooms for municipal water utilities, county departments, film studios, and field SMEs.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            className="bg-pink-600 hover:bg-pink-700 text-white font-black uppercase tracking-widest text-xs px-6 py-3.5 rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
                        >
                            <Plus size={16} /> {isFormOpen ? 'Close Form' : 'Provision TCM Workspace'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Comprehensive Creation / Registration Form (Matching onboarding-tcm.html 100%) */}
            {isFormOpen && (
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                      <div>
                          <h2 className="text-base font-black uppercase tracking-widest text-pink-400 flex items-center gap-2">
                              <Server size={18} /> Provision Enterprise TCM Workspace (100% Onboarding Schema)
                          </h2>
                          <p className="text-xs text-zinc-400 mt-1">
                              Deploy dedicated subdomain routing, capacity seats, automated triage, and SuperAdmin ownership.
                          </p>
                      </div>
                      <button 
                          onClick={() => setIsFormOpen(false)}
                          className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
                      >
                          <X size={18} />
                      </button>
                  </div>

                  <form onSubmit={handleCreateTenant} className="space-y-6">
                      {/* Section 1: Entity Profile & Subdomain */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 flex items-center gap-2">
                              <Building size={14} /> 1. Organization &amp; Subdomain Routing
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Organization Legal Name *</label>
                                  <input 
                                      type="text" 
                                      value={orgName} 
                                      onChange={e => setOrgName(e.target.value)} 
                                      placeholder="e.g. Chemosit Water & Sanitation Co. (CHEWSCO)" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Organization Type *</label>
                                  <select 
                                      value={orgType} 
                                      onChange={e => setOrgType(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500 font-bold"
                                  >
                                      <option>Municipal Water & Sanitation Utility</option>
                                      <option>County Government Department / Ministry</option>
                                      <option>National Agency / Parastatal</option>
                                      <option>Private SME / Corporate Enterprise</option>
                                      <option>Film & Audiovisual Production Studio</option>
                                      <option>NGO / Civic Humanitarian Body</option>
                                      <option>Academic Institution / University</option>
                                      <option>Chama / Community Investment Circle</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Desired Subdomain Slug *</label>
                                  <div className="flex items-center">
                                      <input 
                                          type="text" 
                                          value={subdomainSlug} 
                                          onChange={e => setSubdomainSlug(e.target.value)} 
                                          placeholder="chewsco" 
                                          className="w-full bg-zinc-900 border border-zinc-800 rounded-l-xl px-4 py-3 text-white text-xs font-mono font-bold focus:outline-none focus:border-pink-500"
                                          required
                                      />
                                      <span className="bg-zinc-800 border border-l-0 border-zinc-800 text-zinc-400 px-3 py-3 rounded-r-xl text-[11px] font-mono whitespace-nowrap">
                                          .dynamicindutechno.solutions
                                      </span>
                                  </div>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Registration / Charter No *</label>
                                  <input 
                                      type="text" 
                                      value={orgRegNo} 
                                      onChange={e => setOrgRegNo(e.target.value)} 
                                      placeholder="e.g. WASREB/2019/042" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Website URL</label>
                                  <input 
                                      type="url" 
                                      value={orgWebsite} 
                                      onChange={e => setOrgWebsite(e.target.value)} 
                                      placeholder="https://chewsco.co.ke" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 2: Physical Deployment & Jurisdiction */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 flex items-center gap-2">
                              <MapPin size={14} /> 2. Physical Deployment &amp; Jurisdictional Scope
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Operating Country *</label>
                                  <input 
                                      type="text" 
                                      value={depCountry} 
                                      onChange={e => setDepCountry(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Primary County / Jurisdiction *</label>
                                  <input 
                                      type="text" 
                                      value={depCounty} 
                                      onChange={e => setDepCounty(e.target.value)} 
                                      placeholder="e.g. Kericho / Bomet / Baringo" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Physical HQ Address *</label>
                                  <input 
                                      type="text" 
                                      value={depAddress} 
                                      onChange={e => setDepAddress(e.target.value)} 
                                      placeholder="e.g. Water Works Rd, Block B" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Stations / Field Depots</label>
                                  <select 
                                      value={depBranches} 
                                      onChange={e => setDepBranches(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500 font-bold"
                                  >
                                      <option>Single Central Station (1)</option>
                                      <option>2-5 Regional Stations</option>
                                      <option>6-15 Field Depots</option>
                                      <option>15+ Distributed Field Units</option>
                                  </select>
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Field Staff Mobilization Cadre</label>
                                  <select 
                                      value={depCadre} 
                                      onChange={e => setDepCadre(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500 font-bold"
                                  >
                                      <option>Technicians & Field Officers</option>
                                      <option>Inspectors & Compliance Officers</option>
                                      <option>Mixed Field & Office Desk Staff</option>
                                      <option>Entire Workforce (Office + Operations)</option>
                                  </select>
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Capacity Slider & Operations Modules */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 flex items-center gap-2">
                              <Cpu size={14} /> 3. Capacity Seats &amp; Module Configuration
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <div className="flex justify-between items-center mb-1">
                                      <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Allocated Seats</label>
                                      <span className="text-xs font-mono font-bold text-pink-400">{seatsCount} Seats</span>
                                  </div>
                                  <input 
                                      type="range" 
                                      min="1" 
                                      max="500" 
                                      value={seatsCount} 
                                      onChange={e => handleSeatChange(e.target.value)} 
                                      className="w-full accent-pink-500 cursor-pointer"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Classified Tier</label>
                                  <input 
                                      type="text" 
                                      value={`${tier.toUpperCase()} Tier`} 
                                      readOnly 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-amber-400 font-bold text-xs"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Setup Fee (KES)</label>
                                  <input 
                                      type="number" 
                                      value={setupFee} 
                                      onChange={e => setSetupFee(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono font-bold text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Monthly License Fee (KES)</label>
                                  <input 
                                      type="number" 
                                      value={monthlyUsageFee} 
                                      onChange={e => setMonthlyUsageFee(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-pink-400 font-mono font-bold text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">AI Incident Triage</label>
                                  <select 
                                      value={aiTriageModule} 
                                      onChange={e => setAiTriageModule(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-pink-500"
                                  >
                                      <option>Enabled</option>
                                      <option>Disabled</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">SMS Dispatch Gateway</label>
                                  <select 
                                      value={smsBroadcastModule} 
                                      onChange={e => setSmsBroadcastModule(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-pink-500"
                                  >
                                      <option>Enabled</option>
                                      <option>Disabled</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Offline PWA Sync</label>
                                  <select 
                                      value={offlineSyncModule} 
                                      onChange={e => setOfflineSyncModule(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-pink-500"
                                  >
                                      <option>Enabled</option>
                                      <option>Disabled</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Sensor Telemetry Ingest</label>
                                  <select 
                                      value={telemetryModule} 
                                      onChange={e => setTelemetryModule(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-pink-500"
                                  >
                                      <option>Enabled</option>
                                      <option>Disabled</option>
                                  </select>
                              </div>
                          </div>
                      </div>

                      {/* Section 4: SuperAdmin Credentials */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 flex items-center gap-2">
                              <User size={14} /> 4. Primary SuperAdmin Credentials
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">SuperAdmin Full Name *</label>
                                  <input 
                                      type="text" 
                                      value={adminFullName} 
                                      onChange={e => setAdminFullName(e.target.value)} 
                                      placeholder="e.g. Eng. Kipkemoi Ruto" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Email Address *</label>
                                  <input 
                                      type="email" 
                                      value={adminOfficialEmail} 
                                      onChange={e => setAdminOfficialEmail(e.target.value)} 
                                      placeholder="operations@chewsco.co.ke" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Direct Phone / WhatsApp *</label>
                                  <input 
                                      type="tel" 
                                      value={adminPhone} 
                                      onChange={e => setAdminPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-pink-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Job Title</label>
                                  <input 
                                      type="text" 
                                      value={adminJobTitle} 
                                      onChange={e => setAdminJobTitle(e.target.value)} 
                                      placeholder="e.g. Chief Technical Director" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">National ID / Passport Number</label>
                                  <input 
                                      type="text" 
                                      value={adminNationalId} 
                                      onChange={e => setAdminNationalId(e.target.value)} 
                                      placeholder="e.g. 22883311" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">M-Pesa / Bank Wire Ref</label>
                                  <input 
                                      type="text" 
                                      value={paymentReference} 
                                      onChange={e => setPaymentReference(e.target.value)} 
                                      placeholder="e.g. QWR88119X" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-amber-400 font-mono font-bold text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 5: Documents & Media */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-pink-400 flex items-center gap-2">
                              <FileText size={14} /> 5. Verification Documents &amp; Compliance Links
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Incorporation Charter URL</label>
                                  <input 
                                      type="url" 
                                      value={certIncorporationUrl} 
                                      onChange={e => setCertIncorporationUrl(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">KRA PIN Certificate URL</label>
                                  <input 
                                      type="url" 
                                      value={kraPinUrl} 
                                      onChange={e => setKraPinUrl(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Regulatory License URL</label>
                                  <input 
                                      type="url" 
                                      value={complianceCertUrl} 
                                      onChange={e => setComplianceCertUrl(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-pink-500"
                                  />
                              </div>
                          </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                          <button 
                              type="button" 
                              onClick={() => setIsFormOpen(false)}
                              className="px-6 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest cursor-pointer"
                          >
                              Cancel
                          </button>
                          <button 
                              type="submit" 
                              disabled={statusMessage === 'saving'}
                              className="bg-pink-600 hover:bg-pink-700 text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl transition-all shadow-lg cursor-pointer"
                          >
                              {statusMessage === 'saving' ? 'Provisioning Domain...' : 'Provision Enterprise TCM Workspace'}
                          </button>
                      </div>
                  </form>
                  {statusMessage === 'success' && (
                      <p className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 justify-end">
                          <CheckCircle size={14} /> TCM Workspace provisioned and routed to sub-cluster successfully.
                      </p>
                  )}
              </div>
            )}

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                {/* Spreadsheet Toolbar */}
                <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-4 justify-between items-center bg-zinc-900/60">
                    <div className="flex flex-1 items-center gap-3 min-w-[280px]">
                      <Search size={16} className="text-zinc-400" />
                      <input 
                        type="text"
                        placeholder="Search by workspace, subdomain, county, or SuperAdmin..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-zinc-400" />
                        <select
                            value={filterTier}
                            onChange={e => setFilterTier(e.target.value)}
                            className="bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none"
                        >
                            <option value="all">All Tiers</option>
                            <option value="circle">Circle (1-10)</option>
                            <option value="sphere">Sphere (11-50)</option>
                            <option value="orbit">Orbit (51-500+)</option>
                        </select>
                      </div>

                      <button 
                          onClick={fetchTenants}
                          className="text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-zinc-700 text-zinc-400 bg-zinc-900 hover:text-white transition-all shadow-sm cursor-pointer"
                      >
                          Refresh
                      </button>
                    </div>
                </div>
                
                {/* Horizontal Scrolling High-Density Table with Freeze Panes */}
                <div className="overflow-x-auto relative">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                      <thead>
                          <tr className="bg-zinc-900/90 border-b border-zinc-800 select-none">
                              {/* Freeze Pane: Index */}
                              <th className={`w-12 p-3 text-center text-[10px] font-black uppercase tracking-wider text-zinc-500 border-r border-zinc-800 ${
                                pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-900' : ''
                              }`}>
                                #
                              </th>

                              {/* Freeze Pane: Workspace Name */}
                              <th className={`min-w-[220px] p-0 border-r border-zinc-800 ${
                                pinnedColumn === 'workspaceName' 
                                  ? 'sticky left-12 z-30 bg-zinc-900 border-r-2 border-r-pink-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                  : ''
                              }`}>
                                <div className="w-full h-full p-3 flex items-center justify-between">
                                  <button onClick={() => handleSort('workspaceName')} className="flex items-center gap-1 font-black uppercase tracking-widest text-zinc-300 hover:text-white">
                                    <span>Workspace Name</span> {sortConfig.key === 'workspaceName' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                                  </button>
                                  <button
                                    onClick={() => setPinnedColumn(pinnedColumn === 'workspaceName' ? 'none' : 'workspaceName')}
                                    className={`p-1 rounded cursor-pointer ${pinnedColumn === 'workspaceName' ? 'text-pink-400' : 'text-zinc-600 hover:text-zinc-300'}`}
                                    title={pinnedColumn === 'workspaceName' ? 'Unpin column' : 'Pin Workspace Name'}
                                  >
                                    <Pin size={11} className={pinnedColumn === 'workspaceName' ? 'fill-pink-400' : ''} />
                                  </button>
                                </div>
                              </th>

                              {/* Subdomain */}
                              <th className="min-w-[190px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Subdomain Slug
                              </th>

                              {/* Organization Type */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Organization Type
                              </th>

                              {/* Charter / Reg No */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Reg / Charter No
                              </th>

                              {/* Jurisdiction / County */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Jurisdiction
                              </th>

                              {/* Physical HQ Address */}
                              <th className="min-w-[200px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                HQ Physical Address
                              </th>

                              {/* Stations / Depots */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Stations / Depots
                              </th>

                              {/* Staff Cadre */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Mobilization Cadre
                              </th>

                              {/* Tier & Seats */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Tier &amp; Seats
                              </th>

                              {/* Billing Fees */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                License &amp; Setup Fee
                              </th>

                              {/* Modules (AI / SMS / Offline) */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Active Modules
                              </th>

                              {/* SuperAdmin Contact */}
                              <th className="min-w-[200px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Primary SuperAdmin
                              </th>

                              {/* Admin Phone & ID */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Admin Contact &amp; ID
                              </th>

                              {/* Payment Ref */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Payment Ref
                              </th>

                              {/* Status */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Status
                              </th>

                              {/* Actions */}
                              <th className="min-w-[110px] p-3 text-center font-black uppercase tracking-widest text-zinc-400">
                                Action
                              </th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-medium">
                          {loading ? (
                              <tr><td colSpan="17" className="p-8 text-center text-zinc-500 text-xs">Loading TCM Spreadsheets...</td></tr>
                          ) : sortedTenants.length === 0 ? (
                              <tr><td colSpan="17" className="p-8 text-center text-zinc-500 text-xs">No TCM workspaces found matching criteria.</td></tr>
                          ) : sortedTenants.map((tenant, idx) => {
                              const workspaceName = tenant.workspaceName || tenant.orgName || 'Workspace';
                              const sub = tenant.subdomain || tenant.subdomainSlug || 'ops';
                              const tierVal = tenant.tier || (tenant.seatsCount > 50 ? 'orbit' : tenant.seatsCount > 10 ? 'sphere' : 'circle');

                              return (
                                <tr 
                                    key={tenant.id} 
                                    onClick={() => setSelectedTenant(tenant)}
                                    className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                                >
                                    {/* Pinned: Index */}
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 border-r border-zinc-800 ${
                                      pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>

                                    {/* Pinned: Workspace Name */}
                                    <td className={`p-3 border-r border-zinc-800 ${
                                      pinnedColumn === 'workspaceName' 
                                        ? 'sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r-2 border-r-pink-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                        : ''
                                    }`}>
                                        <p className="font-bold text-white group-hover:text-pink-400 transition-colors">{workspaceName}</p>
                                        <p className="text-[10px] text-zinc-500 font-mono">{tenant.orgType || 'SME'}</p>
                                    </td>

                                    {/* Subdomain */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-pink-300">
                                        {sub}.dynamicindutechno.solutions
                                    </td>

                                    {/* Organization Type */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {tenant.orgType || 'Municipal Utility'}
                                    </td>

                                    {/* Reg No */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-zinc-400">
                                        {tenant.orgRegNo || tenant.regNo || '-'}
                                    </td>

                                    {/* Jurisdiction */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300 font-semibold">
                                        {tenant.depCounty || tenant.county || 'Kenya'}
                                    </td>

                                    {/* HQ Address */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-400 truncate max-w-[200px]" title={tenant.depAddress}>
                                        {tenant.depAddress || tenant.address || '-'}
                                    </td>

                                    {/* Stations */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-400">
                                        {tenant.depBranches || '1 Station'}
                                    </td>

                                    {/* Cadre */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {tenant.depCadre || 'Technicians & Field'}
                                    </td>

                                    {/* Tier & Seats */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className="font-bold text-amber-400 uppercase text-[11px] block">{tierVal} Tier</span>
                                        <span className="font-mono text-[10px] text-zinc-400">{tenant.seatsCount || 10} Allocated Seats</span>
                                    </td>

                                    {/* Fees */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <div className="font-mono font-bold text-pink-400">
                                          KES {(tenant.monthlyUsageFee || ((tenant.seatsCount || 1) * 1500)).toLocaleString()} / mo
                                        </div>
                                        <div className="text-[10px] text-zinc-500 font-mono">
                                          Setup: KES {(tenant.setupFee || 5000).toLocaleString()}
                                        </div>
                                    </td>

                                    {/* Modules */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase ${tenant.aiTriageModule !== false && tenant.aiTriageModule !== 'Disabled' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-zinc-900 text-zinc-600'}`}>
                                                AI
                                            </span>
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase ${tenant.smsBroadcastModule !== false && tenant.smsBroadcastModule !== 'Disabled' ? 'bg-blue-950 text-blue-400 border border-blue-800' : 'bg-zinc-900 text-zinc-600'}`}>
                                                SMS
                                            </span>
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase ${tenant.offlineSyncModule !== false && tenant.offlineSyncModule !== 'Disabled' ? 'bg-purple-950 text-purple-400 border border-purple-800' : 'bg-zinc-900 text-zinc-600'}`}>
                                                SYNC
                                            </span>
                                        </div>
                                    </td>

                                    {/* Primary Admin */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <p className="text-white font-bold">{tenant.primaryAdminName || tenant.adminFullName || '-'}</p>
                                        <p className="font-mono text-[10px] text-zinc-500">{tenant.primaryAdminEmail || tenant.email || '-'}</p>
                                    </td>

                                    {/* Admin Phone & ID */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-zinc-400">
                                        <div>{tenant.primaryAdminPhone || tenant.adminPhone || '-'}</div>
                                        <div className="text-[10px] text-zinc-600">ID: {tenant.primaryAdminNationalId || tenant.adminNationalId || '-'}</div>
                                    </td>

                                    {/* Payment Ref */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-amber-400">
                                        {tenant.paymentReference || tenant.transactionRef || '-'}
                                    </td>

                                    {/* Status */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                                          tenant.status === 'active' 
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                          {tenant.status || 'Active'}
                                        </span>
                                    </td>

                                    {/* Action */}
                                    <td className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => setSelectedTenant(tenant)}
                                                className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                                title="Inspect Full Workspace Dossier"
                                            >
                                                <Eye size={12} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(tenant.id)}
                                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Workspace"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            </div>

            {/* Slide-over Full Dossier Drawer */}
            <TcmDetailDrawer 
              tenant={selectedTenant} 
              onClose={() => setSelectedTenant(null)} 
              onUpdateStatus={handleUpdateStatus}
            />
        </div>
    );
};

export default TcmManager;
